import { useState } from "react";
import { withTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useRemoveSelectedItemFromCloset, useReloadCustomizableCloset } from "@hooks/use-customizable-closet";
import { useSelectItems } from '@hooks/use-buy-closet-items';
import Link from "@components/core/Link";
import Button from "@components/core/Button";
import closetStates from "@utils/constants/closetStates";

function ClosetItemButtons({
  closetState,
  closetItem,
  closetHasMailboxPickingRequested,
  fromDrawer,
  t,
}) {
  const router = useRouter();

  const removeSelectedItemFromCloset = useRemoveSelectedItemFromCloset();
  const reloadCustomizableCloset = useReloadCustomizableCloset();
  const selectItems = useSelectItems();

  const [deletingSelectedItem, setDeletingSelectedItem] = useState(false);

  const product = closetItem?.product || {};

  async function deleteClosetItem() {
    setDeletingSelectedItem(true);

    try {
      await removeSelectedItemFromCloset(closetItem.id);
    } catch (error) {
      setDeletingSelectedItem(false);
      reloadCustomizableCloset();
    }
  }

  function rateClosetItemPathname() {
    if (closetItem.review?.id) {
      return `/reviews/${closetItem.review.id}/edit`;
    }

    return `/reviews`;
  }

  function changeButtons() {
    return (
      <div className="bg-light border-top border-light d-flex" style={{ gap: "1px" }}>
        <div className="d-flex bg-white w-50" style={{ flex: 1 }}>
          <Link
            href={`/products/${product.slug}`}
            className="w-100"
          >
            <Button
              variant="ghost-primary"
              fullWidth
            >
              {t("common:drawer.card.modify")}
            </Button>
          </Link>
        </div>
        <div className="d-flex bg-white w-50" style={{ flex: 1 }}>
          <Button
            variant="ghost-primary"
            fullWidth
            onClick={deleteClosetItem}
            loading={deletingSelectedItem}
          >
            {t("common:drawer.card.remove")}
          </Button>
        </div>
      </div>
    );
  }

  function rateAndBuyButtons() {
    return (
      <div className="bg-light border-top border-light d-flex" style={{ gap: "1px" }}>
        <div className="d-flex bg-white w-50" style={{ flex: 1 }}>
          <Link
            href={{
              pathname: rateClosetItemPathname(),
              query: {
                id: closetItem.id,
                redirect: router.asPath,
                origin: fromDrawer ? "closetDrawer" : "other",
              },
            }}
            className="w-100"
          >
            <Button
              variant="ghost-primary"
              fullWidth
            >
              {closetItem.review?.id ? (
                t("common:drawer.card.rating", { rating: closetItem.review.rating })
              ) : (
                t("common:drawer.card.rate")
              )}
            </Button>
          </Link>
        </div>

        {closetState === closetStates.received && !closetHasMailboxPickingRequested && !closetItem.purchased && !closetItem.purchasing && (
          <div className="d-flex bg-white w-50" style={{ flex: 1 }}>
            <Link
              href={`/closets/${closetItem.closet_id}/buy_items`}
              className="w-100"
            >
              <Button
                variant="ghost-primary"
                fullWidth
                onClick={() => selectItems([closetItem.id])}
              >
                {t("common:drawer.card.buy")}
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  }

  switch(closetState) {
    case closetStates.readyToChange:
      return changeButtons();
    case closetStates.received:
    case closetStates.postReturned:
    case closetStates.returned:
    case closetStates.purchased:
      return rateAndBuyButtons();
    default:
      return null;
  }
}

export default withTranslation("common")(ClosetItemButtons);
